import React from 'react'
import { CancellationReasonDto, CaseOriginDto, CasePriorityDto, CaseReasonDto, CaseStatusDto, CaseSubReasonDto, CaseSubStatusDto, CaseTypeDto, CompetitorDto } from '@v1CasesApi'
import { DeletedStateEnum, UsersDto } from '@v1UsersApi'
import { DealerContactDto } from '@v1DealersApi'
import { V1ApiCasesClient, V1ApiUsersClient, V1ApiDealersClient } from 'core/http-common'
import { toast } from './toast'
import { IDropDownValue } from 'types'

export const fetchData = async <TIData extends unknown>(getData: Function, id?: number) => {
    let dataList: Array<TIData> = []
    try {
        let atEnd = false
        let page = 0
        //Get all the data.
        while (!atEnd) {
            const response = id ? await getData(page + 1, id) : await getData(page + 1)
            if (response.data && response.data.length > 0) {
                dataList = [...dataList, ...response.data]
            } else {
                atEnd = true
            }
            page = response.page
            if (response.page == undefined || response.page >= response.totalPages)
                atEnd = true
        }
    }
    catch (e) {
        toast.error('Error Fetching Data')
    }
    finally {
        return dataList
    }
}


const fetchCaseStatuses = async (page: number) => {
    return await V1ApiCasesClient.getCaseStatuses(undefined, undefined, undefined, page, 1000)
}

const fetchCaseSubStatuses = async (page: number, id: number | undefined) => {
    if (id) {
        return await V1ApiCasesClient.getCaseSubStatusesByStatusId(id, undefined, undefined, undefined, page, 1000)
    }
    return {}
}

const fetchCaseTypes = async (page: number) => {
    return await V1ApiCasesClient.getCaseTypes(undefined, undefined, undefined, page, 1000)
}

const fetchCaseReasons = async (page: number) => {
    return await V1ApiCasesClient.getCaseReasons(undefined, undefined, undefined, page, 1000)
}

const fetchCaseSubResons = async (page: number, id: number | undefined) => {
    if (id) {
        return await V1ApiCasesClient.getCaseSubReasonsByCaseReasonId(id, undefined, undefined, undefined, page, 1000)
    }
    return {}
}

const fetchCasePriorities = async (page: number) => {
    return await V1ApiCasesClient.getCasePriorities(undefined, undefined, undefined, page, 1000)
}

const fetchCaseOrigins = async (page: number) => {
    return await V1ApiCasesClient.getCaseOrigins(undefined, undefined, undefined, page, 1000)
}

const fetchActiveUsers = async (page: number) => {
    return await V1ApiUsersClient.getUsers(DeletedStateEnum.Active, undefined, undefined, undefined, page, 1000)
}

const fetchAllUsers = async (page: number) => {
    return await V1ApiUsersClient.getUsers(DeletedStateEnum.Both, undefined, undefined, undefined, page, 1000)
}

const fetchDealerContacts = async (page: number, id: number | undefined) => {
    if (id) {
        return await V1ApiDealersClient.getContacts(id, undefined, undefined, undefined, page, 1000)
    }
    return {}
}

const fetchCompetitors = async (page: number) => {
    return await V1ApiCasesClient.getCompetitors(undefined, undefined, undefined, page, 1000)
}

const fetchCancellationReasons = async (page: number) => {
    return await V1ApiCasesClient.getCancellationReasons(undefined, undefined, undefined, page, 1000)
}

const fetchApproveCaseUsers = async (page: number) => {
    return await V1ApiUsersClient.getApproveCaseUsers(undefined, undefined, undefined, page, 1000)
}

export const typeData = {

    async caseStatuses() {
        const statusData: Array<CaseStatusDto> = await fetchData<CaseStatusDto>(fetchCaseStatuses)
        const dropDownData: Array<IDropDownValue> = []
        statusData.map(cs => dropDownData.push({ id: cs.caseStatusId, text: cs.caseStatus1 ? cs.caseStatus1 : '' }))
        return dropDownData
    },

    async caseSubStatuses(statusId?: number) {
        const statusSubData: Array<CaseSubStatusDto> = await fetchData<CaseSubStatusDto>(fetchCaseSubStatuses, statusId)
        const dropDownData: Array<IDropDownValue> = []
        statusSubData.map(cs => dropDownData.push({ id: cs.caseSubStatusId, text: cs.caseSubStatus1 ? cs.caseSubStatus1 : '' }))
        return dropDownData
    },

    async caseTypes() {
        const typeData: Array<CaseTypeDto> = await fetchData<CaseTypeDto>(fetchCaseTypes)
        const dropDownData: Array<IDropDownValue> = []
        typeData.map(cs => dropDownData.push({ id: cs.caseTypeId, text: cs.caseType1 ? cs.caseType1 : '' }))
        return dropDownData
    },

    async caseReason() {
        const reasonData: Array<CaseReasonDto> = await fetchData<CaseReasonDto>(fetchCaseReasons)
        const dropDownData: Array<IDropDownValue> = []
        reasonData.map(cs => dropDownData.push({ id: cs.caseReasonId, text: cs.caseReason1 ? cs.caseReason1 : '' }))
        return dropDownData
    },

    async caseSubReasons(reasonId?: number) {
        const reasonSubData: Array<CaseSubReasonDto> = await fetchData<CaseSubReasonDto>(fetchCaseSubResons, reasonId)
        const dropDownData: Array<IDropDownValue> = []
        reasonSubData.map(cs => dropDownData.push({ id: cs.caseSubReasonId, text: cs.caseSubReason1 ? cs.caseSubReason1 : '' }))
        return dropDownData
    },

    async casePriorities() {
        const priorityData: Array<CasePriorityDto> = await fetchData<CasePriorityDto>(fetchCasePriorities)
        const dropDownData: Array<IDropDownValue> = []
        priorityData.map(cs => dropDownData.push({ id: cs.casePriorityId, text: cs.casePriority1 ? cs.casePriority1 : '' }))
        return dropDownData
    },

    async caseOrigins() {
        const originData: Array<CaseOriginDto> = await fetchData<CaseOriginDto>(fetchCaseOrigins)
        const dropDownData: Array<IDropDownValue> = []
        originData.map(cs => dropDownData.push({ id: cs.caseOriginId, text: cs.caseOrigin1 ? cs.caseOrigin1 : '' }))
        return dropDownData
    },

    async activeUsers() {
        const activeUserData: Array<UsersDto> = await fetchData<UsersDto>(fetchActiveUsers)
        const dropDownData: Array<IDropDownValue> = []
        activeUserData.map(cs => dropDownData.push({ id: cs.usersId, text: cs.firstName + ' ' + cs.lastName }))
        return dropDownData
    },

    async allUsers() {
        const allUserData: Array<UsersDto> = await fetchData<UsersDto>(fetchAllUsers)
        const dropDownData: Array<IDropDownValue> = []
        allUserData.map(au => dropDownData.push({ id: au.usersId, text: au.firstName + ' ' + au.lastName }))
        return dropDownData
    },

    async dealerContacts(dealerId?: number) {
        const delaerContactData: Array<DealerContactDto> = await fetchData<DealerContactDto>(fetchDealerContacts, dealerId)
        const dropDownData: Array<IDropDownValue> = []
        delaerContactData.map(cs => dropDownData.push({ id: cs.dealerContactId, text: cs.firstName + ' ' + cs.lastName + ' - ' + cs.phone + ' - ' + cs.email }))
        return dropDownData
    },

    async competitors() {
        const competitorData: Array<CompetitorDto> = await fetchData<CompetitorDto>(fetchCompetitors)
        const dropDownData: Array<IDropDownValue> = []
        competitorData.map(cd => dropDownData.push({ id: cd.competitorId, text: cd.competitorName ? cd.competitorName : '' }))
        return dropDownData
    },

    async cancellationReasons() {
        const reasonData: Array<CancellationReasonDto> = await fetchData<CancellationReasonDto>(fetchCancellationReasons)
        const dropDownData: Array<IDropDownValue> = []
        reasonData.map(rd => dropDownData.push({ id: rd.cancellationReasonId, text: rd.cancellationReason1 ? rd.cancellationReason1 : '' }))
        return dropDownData
    },

    async approveCaseUsers() {
        const activeUserData: Array<UsersDto> = await fetchData<UsersDto>(fetchApproveCaseUsers)
        const dropDownData: Array<IDropDownValue> = []
        activeUserData.map(cs => dropDownData.push({ id: cs.usersId, text: cs.firstName + ' ' + cs.lastName }))
        return dropDownData
    },

}